import React from "react"
import { Button } from "./ui/button"  
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card"
import {
  Calendar,
  Check,
  Cloud,
  Leaf,
  Camera,
  BookOpen,
  ArrowRight,
  Heart,
  Mail,
  Phone,
  MapPin,
  Facebook,
  Instagram,
  Twitter,
  Star,
} from "lucide-react"
import { Badge } from "./ui/badge"
import { useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import InteractiveDashboard from './InteractiveDashboard';
import LearnMoreModal from './LearnMoreModal';
import SharedNavbar from './SharedNavbar';

const features = [
  {
    icon: BookOpen,
    titleKey: "features.catalog.title",
    descriptionKey: "features.catalog.description",
    color: "from-emerald-500 to-green-600",
  },
  {
    icon: Calendar,
    titleKey: "features.calendar.title",
    descriptionKey: "features.calendar.description",
    color: "from-blue-500 to-cyan-600",
  },
  {
    icon: Cloud,
    titleKey: "features.weather.title",
    descriptionKey: "features.weather.description",
    color: "from-purple-500 to-indigo-600",
  },
  {
    icon: Camera,
    titleKey: "features.tracking.title",
    descriptionKey: "features.tracking.description",
    color: "from-orange-500 to-red-600",
  },
]

const pricingPlans = [
  {
    nameKey: "pricing.free.name",
    price: "0",
    periodKey: "pricing.free.period",
    descriptionKey: "pricing.free.description",
    featuresKey: "pricing.free.features",
    popular: false,
    ctaKey: "pricing.free.cta",
  },
  {
    nameKey: "pricing.premium.name",
    price: "4.99",
    periodKey: "pricing.premium.period",
    descriptionKey: "pricing.premium.description",
    featuresKey: "pricing.premium.features",
    popular: true,
    ctaKey: "pricing.premium.cta",
  },
  {
    nameKey: "pricing.pro.name",
    price: "9.99",
    periodKey: "pricing.pro.period",
    descriptionKey: "pricing.pro.description",
    featuresKey: "pricing.pro.features",
    popular: false,
    ctaKey: "pricing.pro.cta",
  },
]

const testimonials = [
  {
    nameKey: "testimonials.user1.name",
    locationKey: "testimonials.user1.location", 
    textKey: "testimonials.user1.text",
    rating: 5,
  },
  {
    nameKey: "testimonials.user2.name",
    locationKey: "testimonials.user2.location",
    textKey: "testimonials.user2.text", 
    rating: 5,
  },
  {
    nameKey: "testimonials.user3.name",
    locationKey: "testimonials.user3.location",
    textKey: "testimonials.user3.text",
    rating: 5,
  },
]

export default function LandingPage() {
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false)
  const navigate = useNavigate()
  const { t, i18n } = useTranslation();

  // Console logging when Landing Page is rendered
  React.useEffect(() => {
    console.log('🌱 Kiemo Draugas - Landing Page Rendered:');
    console.log('📄 Page: Main Landing Page');
    console.log('🌍 Language: ' + i18n.language);
    console.log('🔓 Access: Full site accessible');
    console.log('─'.repeat(50));
  }, [i18n.language]);

  const handleGetStarted = () => {
    navigate('/register')
  }

  const handleLogin = () => {
    navigate('/login')
  }

  const handleLearnMore = () => {
    setIsLearnMoreOpen(true)
  }

  return (
    <div className="min-h-screen bg-white">
      <SharedNavbar onLogin={handleLogin} onGetStarted={handleGetStarted} />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=60 height=60 viewBox=0 0 60 60 xmlns=http://www.w3.org/2000/svg%3E%3Cg fill=none fillRule=evenodd%3E%3Cg fill=%2310b981 fillOpacity=0.05%3E%3Cpath d=M30 30c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zm0 0c0 11.046 8.954 20 20 20s20-8.954 20-20-8.954-20-20-20-20 8.954-20 20z/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] bg-repeat"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 pt-20 text-center lg:text-left">
              <div className="space-y-6">
                <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight animate-fade-in-up">
                  {t('hero.title')}
                </h1>
                <p className="text-lg text-emerald-600 font-medium mb-4 animate-fade-in-up animation-delay-200">
                  Jūsų asmeninis sodo asistentas kišenėje
                </p>
                <p className="text-xl text-gray-600 leading-relaxed animate-fade-in-up animation-delay-400">
                  {t('hero.subtitle')}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-600">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-lg px-8 py-6" onClick={handleGetStarted}>
                  {t('hero.getStarted')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-lg px-8 py-6 bg-transparent"
                  onClick={handleLearnMore}
                >
                  {t('hero.learnMore')}
                </Button>
              </div>
            </div>

            <div className="relative animate-float">
              <InteractiveDashboard />
              
              {/* Floating notification cards */}
              <div className="absolute -top-4 -right-4 bg-white rounded-lg shadow-lg p-3 animate-bounce border border-gray-200">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-500" />
                  <span className="text-xs font-medium text-gray-700">Laistyti šiandien</span>
                </div>
              </div>

              <div className="absolute -bottom-4 -left-4 bg-white rounded-lg shadow-lg p-3 animate-pulse border border-gray-200">
                <div className="flex items-center gap-2">
                  <Heart className="w-4 h-4 text-red-500" />
                  <span className="text-xs font-medium text-gray-700">98% sveikų augalų</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">{t('features.title')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('features.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="group border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white"
              >
                <CardHeader className="text-center pb-4">
                  <div
                    className={`w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-emerald-700 transition-colors">
                    {t(feature.titleKey)}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-center leading-relaxed">
                    {t(feature.descriptionKey)}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">{t('testimonials.title')}</h2>
            <p className="text-xl text-gray-600">
              {t('testimonials.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{t(testimonial.textKey)}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                      <span className="text-emerald-700 font-semibold text-sm">
                        {t(testimonial.nameKey)
                          .split(" ")
                          .map((n: string) => n[0])
                          .join("")}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{t(testimonial.nameKey)}</p>
                      <p className="text-sm text-gray-500">{t(testimonial.locationKey)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">{t('pricing.title')}</h2>
            <p className="text-xl text-gray-600">{t('pricing.subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <Card
                key={index}
                className={`relative border-2 transition-all duration-300 hover:shadow-xl ${
                  plan.popular ? "border-emerald-500 shadow-lg scale-105" : "border-gray-200 hover:border-emerald-300"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-emerald-600 text-white px-4 py-1">
                      {t('pricing.mostPopular')}
                    </Badge>
                  </div>
                )}

                <CardHeader className="text-center pb-8">
                  <CardTitle className="text-2xl font-bold text-gray-900">{t(plan.nameKey)}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-gray-900">€{plan.price}</span>
                    <span className="text-gray-500">/{t(plan.periodKey)}</span>
                  </div>
                  <CardDescription className="mt-2">{t(plan.descriptionKey)}</CardDescription>
                </CardHeader>

                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    {(() => {
                      const features = t(plan.featuresKey, { returnObjects: true });
                      if (Array.isArray(features)) {
                        return features.map((feature: any, featureIndex: number) => (
                          <li key={featureIndex} className="flex items-center gap-3">
                            <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                            <span className="text-gray-700">{String(feature)}</span>
                          </li>
                        ));
                      }
                      return null;
                    })()}
                  </ul>

                  <Button
                    className={`w-full mt-8 ${
                      plan.popular ? "bg-emerald-600 hover:bg-emerald-700" : "bg-gray-900 hover:bg-gray-800"
                    }`}
                    size="lg"
                    onClick={handleGetStarted}
                  >
                    {t(plan.ctaKey)}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              {t('pricing.needMoreInfo')}
            </p>
            <Button
              variant="outline"
              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 bg-transparent"
            >
              {t('pricing.contactUs')}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-600 text-white">
                  <Leaf className="h-5 w-5" />
                </div>
                <div className="flex flex-col">
                  <span className="text-lg font-bold">{t('branding.appName')}</span>
                  <span className="text-xs text-gray-400">{t('branding.tagline')}</span>
                </div>
              </div>
              <p className="text-gray-400 leading-relaxed">
                {t('footer.description')}
              </p>
              <div className="flex space-x-4">
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white">
                  <Facebook className="w-5 h-5" />
                </Button>
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white">
                  <Instagram className="w-5 h-5" />
                </Button>
                <Button size="sm" variant="ghost" className="text-gray-400 hover:text-white">
                  <Twitter className="w-5 h-5" />
                </Button>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">{t('footer.product')}</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a href="#features" className="hover:text-white transition-colors">
                    {t('navigation.features')}
                  </a>
                </li>
                <li>
                  <a href="#pricing" className="hover:text-white transition-colors">
                    {t('navigation.pricing')}
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    {t('footer.links.api')}
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    {t('footer.links.mobileApp')}
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">{t('footer.links.help')}</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    {t('footer.links.documentation')}
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    {t('footer.links.support')}
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    {t('footer.links.faq')}
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    {t('footer.links.community')}
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">{t('footer.links.contacts')}</h3>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>{t('footer.contactInfo.email')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>{t('footer.contactInfo.phone')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>{t('footer.contactInfo.address')}</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">{t('footer.legal.copyright')}</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">
                {t('footer.legal.privacy')}
              </a>
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">
                {t('footer.legal.terms')}
              </a>
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">
                {t('footer.legal.cookies')}
              </a>
            </div>
          </div>
                 </div>
       </footer>

       {/* Learn More Modal */}
       <LearnMoreModal
         isOpen={isLearnMoreOpen}
         onClose={() => setIsLearnMoreOpen(false)}
         language={i18n.language}
       />
     </div>
   )
 } 